import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Avatar,
} from '@mui/material';
import {
  DirectionsSubway,
  Speed,
  Security,
  Support,
  Route,
  Notifications,
  LocationOn,
  Timeline,
} from '@mui/icons-material';

const FeaturesSection: React.FC = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <DirectionsSubway sx={{ fontSize: 32, color: '#667eea' }} />,
      title: 'Smart Route Planning',
      description: 'AI-powered algorithms find the fastest and most efficient routes between metro stations with real-time optimization.',
      color: '#667eea',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      icon: <Speed sx={{ fontSize: 32, color: '#4CAF50' }} />,
      title: 'Real-time Updates',
      description: 'Get live information about train schedules, delays, platform changes, and service disruptions.',
      color: '#4CAF50',
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    },
    {
      icon: <Security sx={{ fontSize: 32, color: '#FF9800' }} />,
      title: 'Secure & Private',
      description: 'Your data is protected with enterprise-grade security, encryption, and privacy measures.',
      color: '#FF9800',
      gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
    },
    {
      icon: <Route sx={{ fontSize: 32, color: '#E91E63' }} />,
      title: 'Multi-City Support',
      description: 'Navigate metro networks across multiple cities with comprehensive station and line coverage.',
      color: '#E91E63',
      gradient: 'linear-gradient(135deg, #E91E63 0%, #C2185B 100%)',
    },
    {
      icon: <Notifications sx={{ fontSize: 32, color: '#9C27B0' }} />,
      title: 'Smart Notifications',
      description: 'Receive personalized alerts for your routes, delays, and important metro updates.',
      color: '#9C27B0',
      gradient: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
    },
    {
      icon: <LocationOn sx={{ fontSize: 32, color: '#2196F3' }} />,
      title: 'Station Discovery',
      description: 'Find nearby stations, facilities, and amenities with detailed station information.',
      color: '#2196F3',
      gradient: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
    },
  ];

  return (
    <Box sx={{ py: 12, bgcolor: '#0f0f23' }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label="Features"
            sx={{
              bgcolor: 'rgba(102, 126, 234, 0.2)',
              color: '#667eea',
              fontWeight: 600,
              mb: 2,
              border: '1px solid rgba(102, 126, 234, 0.3)',
            }}
          />
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              color: 'white',
            }}
          >
            Why Choose MetroRahi?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontWeight: 400,
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Experience the future of metro navigation with our comprehensive suite of intelligent features
          </Typography>
        </Box>

        {/* Features Grid */}
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  borderRadius: 4,
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  bgcolor: '#1a1a2e',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                    '& .feature-icon': {
                      transform: 'scale(1.1)',
                    },
                  },
                  overflow: 'visible',
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 4, textAlign: 'center' }}>
                  {/* Icon Container */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 3,
                    }}
                  >
                    <Avatar
                      className="feature-icon"
                      sx={{
                        width: 80,
                        height: 80,
                        background: feature.gradient,
                        transition: 'transform 0.3s ease',
                        boxShadow: `0 8px 25px ${feature.color}40`,
                      }}
                    >
                      {feature.icon}
                    </Avatar>
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{
                      mb: 2,
                      fontWeight: 700,
                      color: 'white',
                    }}
                  >
                    {feature.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'rgba(255,255,255,0.8)',
                      lineHeight: 1.6,
                      fontSize: '1rem',
                    }}
                  >
                    {feature.description}
                  </Typography>

                  {/* Learn More Link */}
                  <Box sx={{ mt: 3 }}>
                    <Chip
                      label="Learn More"
                      size="small"
                      sx={{
                        bgcolor: `${feature.color}20`,
                        color: feature.color,
                        fontWeight: 600,
                        cursor: 'pointer',
                        border: `1px solid ${feature.color}40`,
                        '&:hover': {
                          bgcolor: `${feature.color}30`,
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: 'white',
            }}
          >
            Ready to transform your metro experience?
          </Typography>
          <Chip
            icon={<Timeline />}
            label="Explore All Features"
            sx={{
              bgcolor: '#667eea',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 600,
              px: 3,
              py: 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#5a6fd8',
              },
            }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturesSection; 